import { memo, useCallback, useState } from "react";
import tw, { theme } from "twin.macro";

import Label from "@atoms/Label";
import Button from "@atoms/Button";
import Heading from "@atoms/Heading";
import Paragraph from "@atoms/Paragraph";
import CounterInput from "@atoms/CounterInput";
import ProductCardTag from "@atoms/ProductCardTag";
import Image from "@atoms/Image";
import Caption from "@atoms/Caption";
import ModalItemsOutOfStock from "@organisms/ModalItemsOutOfStock";

import { formatCurrency } from "@utils/money";
import { getDisplayBrand } from "@/utils/product";
import { pageView } from "@/utils/analyticsV2";

import useAppSelector from "@hooks/useAppSelector";
import { useMediaQuery } from "@hooks/useMediaQuery";
import useProductCardGallery from "@hooks/useProductCardGallery";
import type { IProductCardGallery } from "@/types/productGallery";

import { MAX_UNIT_QUANTITY } from "@/recourses/constants";

import IconCart from "@iconsV2/Interrface/cart-01.svg?react";
import IconMessageAlertCircle from "@iconsV2/Communication/message-alert-circle.svg?react";
import IconMessageAlertSquare from "@iconsV2/Communication/message-alert-square.svg?react";

import {
  StyleButton,
  StyledWrapperButton,
  StyledWrapperCard,
  StyledWrapperContent,
  StyledWrapperContentDiscount,
  StyledWrapperContentPrice,
  StyledWrapperContentText,
  StyledWrapperFigure,
  StyledWrapperHelpMessage,
  StyledWrapperTagCustom,
  StyledWrapperTagNewDiscount,
} from "./styles";
import { ECheckoutSource, isImportedItem } from "@/utils/checkoutType";
import OriginTag from "@components/Molecules/OriginTag";

const ProductCardGallery = ({
  item,
  sizeVariant,
  size,
  orientation = "vertical",
  recommId,
  active = false,
  disabled = false,
  withBorder = true,
  ...rest
}: IProductCardGallery) => {
  if (!sizeVariant) {
    throw new TypeError("sizeVariant is required");
  }

  const {
    // states:
    showInput,
    productQuantity,
    isProductAvailable,
    percentageDiscount,
    productFinalPrice,
    productOriginalPrice,
    productFinalPricePerStepWeight,
    gramOrUnitShort,

    // functions:
    getGramans,
    handleAddToCart,
    handledToCleanCart,
    handleIncrement,
    handleToDecrement,
    handleToChangeInput,
    handleGoToBrand,
  } = useProductCardGallery({
    item,
  });

  const [openModalOut, setOpenModalOut] = useState(false);
  const storeId = useAppSelector((state) => state.cart.storeId);
  const isMedium = useMediaQuery("(min-width: {medium})");
  const isSm = sizeVariant === "sm";
  const isLg = sizeVariant === "lg";
  const isVertical = orientation === "vertical";
  const isHorizontal = !isVertical;
  const isHorizontalLg = isLg && isHorizontal;

  const handleAlternatives = useCallback(() => {
    setOpenModalOut(true);
  }, []);

  const TagCustomLabelBesSeller = useCallback(() => {
    // if (item.details?.bestSeller) {
    //   return (
    //     <span className="wrapper-custom-label">
    //       <ProductCardTag
    //         type="bestSeller"
    //         css={isHorizontalLg ? tw`rounded-round shadow-none` : undefined}
    //       />
    //     </span>
    //   );
    // }
    if (item.details.customLabel && item.details.discountLabel) {
      return (
        <span className="wrapper-custom-label">
          <ProductCardTag
            type="customLabel"
            css={isHorizontalLg ? tw`rounded-round shadow-none` : ``}
            label={item.details.discountLabel}
            discountType={item.details.customLabel}
            labelBackground={item.details.customLabelBackgroundColor}
          />
        </span>
      );
    }
    return null;
  }, [
    // item?.bestSeller,
    item?.details.customLabel,
    item?.details.discountLabel,
    item?.details.customLabelBackgroundColor,
    isHorizontalLg,
  ]);

  const TagNewDiscount = useCallback(() => {
    if (item?.details.isNew) {
      return <ProductCardTag type="new" />;
    }

    return null;
  }, [item?.details.isNew]);

  const RemainingUnit = useCallback(
    ({ error }: { error?: boolean }) => {
      return (
        <StyledWrapperHelpMessage>
          <IconMessageAlertCircle css={error && tw`[& > path]:(stroke-error-40)`} />
          <Paragraph
            sizeVariant="sm"
            size={size}
            color={error ? theme`colors.error.40` : theme`colors.neutral.10`}
          >
            Quedan {item.details.maxQtyToOrder} {getGramans(item.details.maxQtyToOrder)}
          </Paragraph>
        </StyledWrapperHelpMessage>
      );
    },
    [item?.details.maxQtyToOrder]
  );

  const HelpMessage = useCallback(() => {
    if (!isProductAvailable) {
      return (
        <StyledWrapperHelpMessage>
          <IconMessageAlertSquare tw="[&>path]:stroke-error-40" />
          <Paragraph sizeVariant="sm" size={size} color={theme`colors.error.40`}>
            No disponible
          </Paragraph>
        </StyledWrapperHelpMessage>
      );
    }

    if (item.details.maxQtyToOrder < MAX_UNIT_QUANTITY || productQuantity > item.details.maxQtyToOrder) {
      return <RemainingUnit error={productQuantity > item.details.maxQtyToOrder} />;
    }
    return null;
  }, [item?.details.maxQtyToOrder, productQuantity]);

  if (!item) {
    return null;
  }

  if (isSm && isHorizontal) {
    return (
      <StyledWrapperCard
        $size={size}
        $orientation={orientation}
        $sizeVariant={sizeVariant}
        $showInputCounter={showInput}
        $isPromoted={item.details.isPromoted}
        $active={active}
        to={recommId ? `/p/${item.product.slug}?recommId=${recommId}` : `/p/${item.product.slug}`}
        title={item.product.name}
        $withBorder={withBorder}
        state={{
          product: item,
        }}
        onClick={async () => {
          await pageView.eventClickButton();
        }}
        {...rest}
      >
        <StyledWrapperContent>
          <StyledWrapperFigure $disabled={disabled || !isProductAvailable}>
            <Image
              src={item.product.image.url}
              alt={item.product.name}
              title={item.product.name}
              effect="blur"
            />
          </StyledWrapperFigure>

          <StyledWrapperContentText>
            <Paragraph
              sizeVariant="sm"
              size={size}
              disabled={disabled || !isProductAvailable}
              tw="line-clamp-3"
            >
              {item.product.name}
            </Paragraph>
          </StyledWrapperContentText>
        </StyledWrapperContent>
        <StyledWrapperContent>
          {!showInput && (
            <StyledWrapperContentPrice>
              <StyledWrapperContentText>
                {productFinalPrice > 0 && (
                  <Label
                    sizeVariant="lg"
                    size={size}
                    color={theme`colors.primaryV2.10`}
                    disabled={disabled || !isProductAvailable}
                  >
                    {formatCurrency(productFinalPrice ?? 0)}
                  </Label>
                )}

                {percentageDiscount > 0 && (
                  <StyledWrapperContentDiscount>
                    <Paragraph
                      sizeVariant="sm"
                      size={size}
                      color={theme`colors.neutral.30`}
                      tw="line-through"
                    >
                      {formatCurrency(productOriginalPrice ?? 0)}
                    </Paragraph>
                  </StyledWrapperContentDiscount>
                )}
              </StyledWrapperContentText>
            </StyledWrapperContentPrice>
          )}

          <StyledWrapperButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <StyleButton>
              {showInput ? (
                <CounterInput
                  className="counter-input"
                  value={String(productQuantity)}
                  showIconRemove={false}
                  size={size}
                  onRemove={handledToCleanCart}
                  onIncrement={() => handleIncrement(+item.product.sku)}
                  onDecrement={handleToDecrement}
                  onChange={(value) => handleToChangeInput(value as number)}
                  maxUnits={item.details.maxQtyToOrder}
                  id="counter-input"
                  error={productQuantity > item.details.maxQtyToOrder}
                  measureType={item.details.measureTypeDisplayShort ?? ""}
                  averageWeight={item.details.averageWeight || 1}
                  isImported={isImportedItem(item)}
                />
              ) : (
                <Button
                  onClick={handleAddToCart}
                  sizeVariant="lg"
                  size={size}
                  fullWidth={!isSm || isVertical}
                  endIcon={<IconCart />}
                  disabled={!isProductAvailable}
                />
              )}
            </StyleButton>
          </StyledWrapperButton>
        </StyledWrapperContent>
      </StyledWrapperCard>
    );
  }

  return (
    <>
      {openModalOut && (
        <ModalItemsOutOfStock
          isOpen={openModalOut}
          handleClose={() => setOpenModalOut(false)}
          closeOutSise
          onlyClose
          storeId={storeId}
          productToVerify={item}
        />
      )}

      <StyledWrapperCard
        $size={size}
        $orientation={orientation}
        $sizeVariant={sizeVariant}
        $showInputCounter={showInput}
        $isPromoted={item.details.isPromoted}
        $active={active}
        to={recommId ? `/p/${item.product.slug}?recommId=${recommId}` : `/p/${item.product.slug}`}
        title={item.product.name}
        $withBorder={withBorder}
        state={{
          product: item,
        }}
        onClick={async () => {
          await pageView.eventClickButton();
        }}
        {...rest}
      >
        <StyledWrapperFigure $disabled={disabled || !isProductAvailable}>
          {!isSm && !isHorizontalLg && <TagCustomLabelBesSeller />}
          <Image
            src={item.product.image.url}
            alt={item.product.name}
            title={item.product.name}
            effect="blur"
          />
          {!isSm && item.details.isPromoted && (
            <span className="wrapper-promoted">
              <ProductCardTag type="promoted" />
            </span>
          )}
        </StyledWrapperFigure>

        <StyledWrapperContent>
          <StyledWrapperContentText>
            {!isSm && (
              <Heading
                type={"h6"}
                disabled={disabled || !isProductAvailable}
                onClick={handleGoToBrand}
                tw="cursor-pointer line-clamp-1"
              >
                {getDisplayBrand(item.product.brand.name)}
              </Heading>
            )}
            <Paragraph
              sizeVariant="sm"
              size={size}
              disabled={disabled || !isProductAvailable}
              tw="line-clamp-2"
            >
              {item.product.name}
            </Paragraph>
          </StyledWrapperContentText>

          <StyledWrapperContentPrice>
            <StyledWrapperContentText>
              {productQuantity > (item.details.averageWeight || 1) && productFinalPricePerStepWeight > 0 && (
                <Caption
                  color={theme`colors.neutral.40`}
                  disabled={disabled || !isProductAvailable}
                  fontWeight={600}
                  size="small"
                >
                  {formatCurrency(productFinalPricePerStepWeight ?? 0)} /{gramOrUnitShort}
                </Caption>
              )}

              {productFinalPrice > 0 && (
                <Label
                  sizeVariant="lg"
                  size={size}
                  color={theme`colors.neutral.10`}
                  disabled={disabled || !isProductAvailable}
                >
                  {formatCurrency(productFinalPrice ?? 0)}
                </Label>
              )}
            </StyledWrapperContentText>

            <StyledWrapperContentText tw="items-end">
              {percentageDiscount > 0 && (
                <Caption
                  color={theme`colors.neutral.40`}
                  disabled={disabled || !isProductAvailable}
                  fontWeight={600}
                  size="small"
                >
                  -{percentageDiscount}%
                </Caption>
              )}

              {item.details.onSale && productOriginalPrice > 0 && (
                <Label sizeVariant="lg" size={size} color={theme`colors.neutral.30`} tw="line-through">
                  {formatCurrency(productOriginalPrice ?? 0)}
                </Label>
              )}
            </StyledWrapperContentText>
          </StyledWrapperContentPrice>

          {!isSm && (
            <StyledWrapperTagNewDiscount>
              {/* Display origin tag to imported products */}
              {isImportedItem(item) && <OriginTag source={ECheckoutSource.INTERNATIONAL} />}
              {/* Display new discount tag */}
              <span tw="col-start-2 justify-self-end">
                <TagNewDiscount />
              </span>
            </StyledWrapperTagNewDiscount>
          )}

          <StyledWrapperButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <HelpMessage />

            <StyleButton>
              {isProductAvailable ? (
                <>
                  {showInput ? (
                    <CounterInput
                      value={String(productQuantity)}
                      showIconRemove={!isSm}
                      size={size}
                      onRemove={handledToCleanCart}
                      onIncrement={() => handleIncrement(+item.product.sku)}
                      onDecrement={handleToDecrement}
                      onChange={(value) => handleToChangeInput(value as number)}
                      maxUnits={item.details.maxQtyToOrder}
                      id="counter-input"
                      error={productQuantity > item.details.maxQtyToOrder}
                      measureType={item.details.measureTypeDisplayShort ?? ""}
                      averageWeight={item.details.averageWeight || 1}
                      isImported={isImportedItem(item)}
                    />
                  ) : (
                    <Button
                      sizeVariant="md"
                      size={size}
                      fullWidth={!isSm || isVertical}
                      endIcon={<IconCart />}
                      disabled={!isProductAvailable}
                      onClick={handleAddToCart}
                    >
                      Agregar
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  color="secondary"
                  sizeVariant="md"
                  size={size}
                  fullWidth={!isSm || isVertical}
                  onClick={handleAlternatives}
                >
                  {isMedium ? "Ver alternativas" : "Alternativas"}
                </Button>
              )}

              {isHorizontalLg && (
                <StyledWrapperTagCustom>
                  <TagCustomLabelBesSeller />
                </StyledWrapperTagCustom>
              )}
            </StyleButton>
          </StyledWrapperButton>
        </StyledWrapperContent>
      </StyledWrapperCard>
    </>
  );
};

export default memo(ProductCardGallery, (prevProps, nextProps) => {
  return (
    prevProps.item.product.id === nextProps.item.product.id &&
    prevProps.sizeVariant === nextProps.sizeVariant &&
    prevProps.orientation === nextProps.orientation &&
    prevProps.item.details.maxQtyToOrder === nextProps.item.details.maxQtyToOrder
  );
});
